/**
 * Builds the object passed to Krypton's `KR.setFormConfig(...)`.
 *
 * Framework-agnostic + side-effect free so it can be unit-tested with
 * `node --test` (the repo's existing test convention) and reused by
 * `components/Form/PayzenCardForm.tsx` without importing React.
 *
 * The baseline config (formToken + kr-language) is preserved EXACTLY as before.
 * When — and ONLY when — `hidePseReceipt` is true, it additionally disables
 * PayZen's own post-payment receipt FOR PSE ONLY, via the official Smart Form
 * option PayZen recommended:
 *
 *   smartForm.paymentMethods.PSE.displayPaymentReceipt = false
 *
 * It is scoped to PSE (never `ALL`), so the card experience is byte-identical.
 * It does NOT change payment-method availability, the REST V4 request, the IPN,
 * ticket emission or the QR — only whether PayZen renders its own receipt page
 * for PSE (the screen that forces the manual "VOLVER A TIKIPAL" tap).
 */

export type PayzenFormConfig = {
  formToken: string;
  'kr-language': string;
  smartForm?: {
    paymentMethods: {
      PSE: { displayPaymentReceipt: boolean };
    };
  };
};

/**
 * Whether PayZen's PSE receipt page should be suppressed so the buyer returns
 * to Tikipal automatically. OFF by default; enabled by setting
 * `NEXT_PUBLIC_PAYZEN_PSE_HIDE_RECEIPT='true'` in the target environment. Strict
 * equality so any other value ('', 'false', '1', 'TRUE', undefined) stays off.
 *
 * The caller MUST pass the raw env value via a direct
 * `process.env.NEXT_PUBLIC_PAYZEN_PSE_HIDE_RECEIPT` member access so Next inlines
 * it at build time. This helper stays a pure comparison (unit-testable) and does
 * NOT read process.env itself, avoiding a non-inlined runtime read on the client.
 */
export function isPseAutoReturnEnabled(value: string | undefined): boolean {
  return value === 'true';
}

export function buildPayzenFormConfig(params: {
  formToken: string;
  language: string;
  hidePseReceipt: boolean;
}): PayzenFormConfig {
  const { formToken, language, hidePseReceipt } = params;

  // Baseline — identical to the pre-existing setFormConfig call.
  const config: PayzenFormConfig = {
    formToken,
    'kr-language': language,
  };

  // Additive, PSE-scoped. Leaves card + every other method untouched.
  if (hidePseReceipt) {
    config.smartForm = {
      paymentMethods: {
        PSE: { displayPaymentReceipt: false },
      },
    };
  }

  return config;
}
